import { CostCenter2, Employee, ExpensePaymentFlowInformationEnum, ExpenseStatusEnum, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { Button, Col, Form, Row, Select, Tooltip } from "antd"
import DeletedTooltip from "Components/deletedTooltip"
import { ExpenseRules } from "Components/forms/rules"
import CostCenter2Modal from "Components/modals/costCenter2.modal"
import { useExpenseFormContext } from "Components/modals/expenseCreateForm.context"
import VendorFormModal from "Components/modals/vendor.modal"
import UserImage from "Components/UserImage"
import { NOT_SET_VALUE } from "Features/pages/expenses/expenseDetails/expenseSplitCells/cell.utils"
import ExpenseInitializer from "Features/pages/expenses/expenseInitializer"
import { changeAllSplitPropertyValues, determinePossibleApproversForExpense, isMileageOrPerDiem } from "Features/pages/expenses/expenseUtils"
import { getActiveWorkflowRule, getApproverForRule, getWorkflowById } from "Features/pages/workflows/workflow.utils"
import WorkflowConditionTag from "Features/pages/workflows/workflowConditionTag"
import { ORANGE } from "@finway-group/shared/lib/consts"

import React, { useEffect } from "react"
import { Plus as PlusIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { FINWAY_ADMIN_EMAIL } from "Shared/config/consts"
import { useModal } from "Shared/context/modal.context"
import { useCompany } from "Shared/hooks/company.hooks"
import { useCostCenterById, useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useEmployees, useLoggedInEmployee } from "Shared/hooks/employee.hooks"
import { useExpense } from "Shared/hooks/expense.hooks"
import { useExpenseAccounts } from "Shared/hooks/expenseAccount.hooks"
import { useCreditorById, useFilteredCreditorsForExpense } from "Shared/hooks/vendor.hooks"
import { useWorkflows } from "Shared/hooks/workflow.hooks"
import { EmployeeService } from "Shared/services"
import { getPopupAnchor } from "Shared/utils"
import { getCostCenterLabel } from "Shared/utils/costCenter.utils"
import { doAllElementsHaveTheSameFieldValue, doFilterSelect, getTooltipPopupContainer, isExpensePaymentFlowInfoEnabled } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { CreditorOptionLabel } from "./creditorNumberDisplay"
import { ExclamationCircleTwoTone } from "@ant-design/icons"
import FormSelect from "Components/form/formSelect"
import { isAdmin } from "Shared/utils/filter.utils"

interface ExpenseCommonInputInterface {
    allowVendorCreation?: boolean
}
const ExpenseCommonInputFieldsForm: React.FC<ExpenseCommonInputInterface> = ({ allowVendorCreation }) => {
    const [{ expenseForm, expense, isInvoiceRequest, isPreApprovedSubscription, isNew, updateExpense, prefilledVendorData }] = useExpenseFormContext()
    const loggedInUser = useLoggedInEmployee()
    const employees = useEmployees(false, false, true)
    const costCenters = useSubCostCenters(true)
    const { t } = useTranslation()
    const workflows = useWorkflows()
    const { showModal } = useModal()
    const { costCenter, vendor, requestedBy, approvalNeededBy } = ExpenseRules(expense, employees)
    const company = useCompany()
    const initialWorkflowData = ExpenseInitializer.getinitializeWorkflowState(
        expense,
        workflows,
        isNew,
        isInvoiceRequest || isPreApprovedSubscription,
        loggedInUser.settings.showGrossAmount
    )
    const [workflow, setWorkflow] = useStateIfMounted(initialWorkflowData)
    const [proposedApprovers, setProposedApprovers] = useStateIfMounted(
        determinePossibleApproversForExpense(expense, employees, costCenters, loggedInUser, workflows, isPreApprovedSubscription, isNew)
    )
    const approvalNeededByValue = expense.approvalNeededBy || proposedApprovers[0]?.id
    const secondApprovalNeededBy = expense.secondApprovalNeededBy ? GetEmployeeById(employees, expense.secondApprovalNeededBy) : undefined
    const activeRule = getActiveWorkflowRule(expense, workflows).activeRule
    const isActiveRuleApproversEqual = secondApprovalNeededBy?.id === approvalNeededByValue
    const isCardExpense = expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD
    const showPaymentFlowInformation = !isCardExpense && !expense.isReimbursement && isExpensePaymentFlowInfoEnabled(company, loggedInUser)
    const isApproverChangeDisabled =
        (expense.paymentFlowInformation &&
            [ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID, ExpensePaymentFlowInformationEnum.REPORTING_ONLY].includes(expense.paymentFlowInformation)) ||
        (workflow && !company?.allowWorkflowAdjustments)
    const shouldShowOnlyVerifiedVendors = [ExpenseStatusEnum.DONE, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DECLINED, ExpenseStatusEnum.REVIEWED].includes(expense.status)

    const expenseAccounts = useExpenseAccounts()
    const costCenters2 = useCostCenters2()
    const allowCostCenter2Creation = (useCompany() as any)?.allowCostCenter2Creation
    const splits = expense.splits
    const isCostCenterChangeable = doAllElementsHaveTheSameFieldValue(splits, "costCenter")
    const isCostCenter2Changeable = doAllElementsHaveTheSameFieldValue(splits, "costCenter2")
    const isExpenseAccountChangeable = doAllElementsHaveTheSameFieldValue(splits, "expenseAccount")
    const { showCostCentersIds } = useCompany()
    const creditors = useFilteredCreditorsForExpense(loggedInUser.activeCompanyProfile.isAdmin, expense)
    const expenseDetails = useExpense()

    const paymentFlowInfoEditDisabled =
        !isNew &&
        (expenseDetails.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY ||
            (expenseDetails.paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_PAID && (expenseDetails.isReviewed() || expenseDetails.isProcessed())))

    // e.g. if the requester was deleted, we still want to show him
    if (expense.requestedBy && !GetEmployeeById(employees, expense.requestedBy)) {
        const requester = EmployeeService.getEmployeeById(expense.requestedBy)
        requester && employees.push(requester)
    }

    useEffect(() => {
        setWorkflow(getWorkflowById(expense.workflow, workflows))
    }, [expense.workflow])

    useEffect(() => {
        setProposedApprovers(determinePossibleApproversForExpense(expense, employees, costCenters, loggedInUser, workflows, isPreApprovedSubscription, true))
    }, [expense])

    const onCostCenterChange = (value: any) => {
        const splitsUpdate = [...splits]
        changeAllSplitPropertyValues(splitsUpdate, "costCenter", value)
        updateExpense({ costCenter: value, splits: splitsUpdate })
    }

    const onPaymentFlowInfoChanged = (value: any) => {
        updateExpense({ paymentFlowInformation: value })
    }

    const onCostCenter2Change = (value: any) => {
        const splitsUpdate = [...splits]
        changeAllSplitPropertyValues(splitsUpdate, "costCenter2", value)
        updateExpense({ costCenter2: value, splits: splitsUpdate })
    }

    const onExpenseAccountChange = (value: any) => {
        const splitsUpdate = [...splits]
        changeAllSplitPropertyValues(splitsUpdate, "expenseAccount", value)
        updateExpense({ expenseAccount: value, splits: splitsUpdate })
    }

    const onRequestedByChange = (value: any) => {
        updateExpense({ requestedBy: value })
    }

    const onVendorChange = (value: any) => {
        updateExpense({ vendor: value, vendorType: creditors.find((i) => i.id === value)?.type as VendorTypeEnum })
    }

    const onApproverChange = (value: any) => {
        updateExpense({
            approvalNeededBy: value,
            // To unset the fields in the back end these should be "null", do not change to "undefined"
            secondApprovalNeededBy: null,
            workflow: null,
            activeWorkflowRule: null,
            approvalsForActiveWorkflowRule: [],
        } as any)
    }

    const onWorkflowChange = (value: any) => {
        const workflow = workflows.find(({ _id }: any) => _id === value)
        const firstRule = workflow?.rules.find(({ order }: any) => order === 1)

        updateExpense({
            approvalNeededBy: getApproverForRule(firstRule?.approverData, expense),
            secondApprovalNeededBy: getApproverForRule(firstRule?.secondApprover, expense),
            workflow: value,
            activeWorkflowRule: firstRule?._id,
            approvalsForActiveWorkflowRule: [],
        })
    }

    const onVendorCreation = (_flag: boolean, vendor: Vendor) => {
        if (vendor) {
            expenseForm.setFieldsValue({ vendor: vendor.id })
            updateExpense({ vendor: vendor.id })
        }
    }

    const onCostCenter2Creation = (_flag: boolean, costCenter2: CostCenter2) => {
        if (costCenter2) {
            // that means we created a new one
            expenseForm.setFieldsValue({ costCenter2: costCenter2._id })
            updateExpense({ costCenter2: costCenter2._id })
        }
    }

    const showVendorModal = () => {
        showModal(VendorFormModal, true, {
            isShowing: true,
            isNew: true,
            prefilledData: prefilledVendorData,
            onCancel: onVendorCreation,
            shouldRedirectOnRestoration: false,
            isDraggable: true,
            isOpenedByExpenseModal: true,
        })
    }

    const showCostCenter2Modal = () => {
        showModal(CostCenter2Modal, true, { isShowing: true, isNew: true, shouldRedirectOnRestoration: false, onCancel: onCostCenter2Creation })
    }
    const renderPaymentFlowSelect = (width: number) => (
        <Col span={width} sm="24">
            <Form.Item label={t("input:request.payment_flow_information.title")} key="paymentFlowInformation" name="paymentFlowInformation">
                <Select
                    getPopupContainer={getPopupAnchor()}
                    defaultValue={NOT_SET_VALUE}
                    value={expense.paymentFlowInformation ?? NOT_SET_VALUE}
                    onSelect={onPaymentFlowInfoChanged}
                    disabled={paymentFlowInfoEditDisabled}
                >
                    {(company?.expensePaymentFlowInformation.isAlreadyPaidEnabled || expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_PAID) && (
                        <Select.Option value={ExpensePaymentFlowInformationEnum.ALREADY_PAID}>
                            {t(`input:request.payment_flow_information.${ExpensePaymentFlowInformationEnum.ALREADY_PAID}`)}
                        </Select.Option>
                    )}
                    {(company?.expensePaymentFlowInformation.isAlreadyApprovedAndPaidEnabled ||
                        expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID) && (
                        <Select.Option value={ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID}>
                            {t(`input:request.payment_flow_information.${ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID}`)}
                        </Select.Option>
                    )}
                    {((loggedInUser.activeCompanyProfile.isAdmin && company?.expensePaymentFlowInformation.isReportingOnlyEnabled) ||
                        expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY) && (
                        <Select.Option value={ExpensePaymentFlowInformationEnum.REPORTING_ONLY}>
                            {t(`input:request.payment_flow_information.${ExpensePaymentFlowInformationEnum.REPORTING_ONLY}`)}
                        </Select.Option>
                    )}
                    <Select.Option value={NOT_SET_VALUE}>{t("input:request.payment_flow_information.not_set")}</Select.Option>
                </Select>
            </Form.Item>
        </Col>
    )
    const selectedCreditor = useCreditorById(expense.vendor)
    const selectedCostCenter = useCostCenterById(expense.costCenter)

    return (
        <>
            <Row gutter={[16, 16]} align="middle">
                {!isMileageOrPerDiem(expense) && (
                    <Col span={allowVendorCreation ? 22 : 24}>
                        <Form.Item label={t("input:request.vendor")} key="vendor" name="vendor" rules={vendor} extra={<p className="input-help">{t("info:vendor_rule")}</p>}>
                            <Select
                                onSelect={onVendorChange}
                                showSearch
                                filterOption={doFilterSelect}
                                getPopupContainer={getPopupAnchor()}
                                placeholder={t("placeholder:vendor.select_a_vendor")}
                            >
                                {selectedCreditor?.source.deleted && (
                                    <Select.Option key={creditors.length} value={expense.vendor} label={selectedCreditor?.name} id={`vendor-option-${creditors.length}`}>
                                        <CreditorOptionLabel creditor={selectedCreditor} />
                                    </Select.Option>
                                )}
                                {creditors.map(
                                    (creditor, index) =>
                                        (!shouldShowOnlyVerifiedVendors || creditor.type !== VendorTypeEnum.VENDOR || (creditor.source as Vendor).isApproved) && (
                                            <Select.Option key={index} value={creditor.id} label={creditor.name} id={`vendor-option-${index}`}>
                                                <CreditorOptionLabel creditor={creditor} />
                                            </Select.Option>
                                        )
                                )}
                            </Select>
                        </Form.Item>
                    </Col>
                )}
                {allowVendorCreation && (
                    <Col span={2} className="mb-14">
                        <Form.Item>
                            <Button className="btn-default" onClick={() => showVendorModal()}>
                                <PlusIcon />
                            </Button>
                        </Form.Item>
                    </Col>
                )}
            </Row>
            {expenseAccounts.length > 0 ? (
                <Row gutter={[16, 16]} align="middle">
                    <Col span={!showPaymentFlowInformation ? 24 : 12} sm="24">
                        <Form.Item label={t("input:request.expense_account")} key="expenseAccount" name="expenseAccount">
                            <Select
                                showSearch
                                filterOption={doFilterSelect}
                                value={expense.expenseAccount || ""}
                                dropdownMatchSelectWidth={false}
                                onSelect={onExpenseAccountChange}
                                disabled={!isExpenseAccountChangeable}
                                placeholder={!isExpenseAccountChangeable ? t("label:splitted") : t("placeholder:expense_account.select_an_expense_account")}
                                getPopupContainer={getPopupAnchor()}
                            >
                                <Select.Option value={NOT_SET_VALUE} label="">
                                    <div className="flex items-center">
                                        <span>{t("missing:request.expense_account")}</span>
                                    </div>
                                </Select.Option>
                                {expenseAccounts.map((expAccount, index) => (
                                    <Select.Option key={index} value={expAccount._id} label={`${expAccount.accountName} ${expAccount.accountCode}`}>
                                        {expAccount.accountCode} - {expAccount.accountName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    {showPaymentFlowInformation && renderPaymentFlowSelect(12)}
                </Row>
            ) : (
                showPaymentFlowInformation && (
                    <Row gutter={[16, 16]} align="middle">
                        {renderPaymentFlowSelect(24)}
                    </Row>
                )
            )}
            <Row gutter={[16, 16]} align="middle">
                <Col span={24} sm="24">
                    <Form.Item label={t("input:request.cost_center")} key="costCenter" name="costCenter" required rules={isCostCenterChangeable ? costCenter : undefined}>
                        <Select
                            onSelect={onCostCenterChange}
                            showSearch
                            filterOption={doFilterSelect}
                            getPopupContainer={getPopupAnchor()}
                            value={expense.costCenter}
                            disabled={!isCostCenterChangeable}
                            placeholder={!isCostCenterChangeable ? t("label:splitted") : t("placeholder:cost_center.select_a_cost_center")}
                        >
                            {selectedCostCenter?.deleted && (
                                <Select.Option
                                    key={costCenters.length}
                                    value={selectedCostCenter._id}
                                    id={`costcenter-option-${costCenters.length}`}
                                    label={getCostCenterLabel(selectedCostCenter, showCostCentersIds)}
                                >
                                    <div>
                                        {getCostCenterLabel(selectedCostCenter, showCostCentersIds)}
                                        <DeletedTooltip translationKey="tooltips:cost_center.was_deleted" deletionDate={selectedCostCenter.dateDeleted} />
                                    </div>
                                </Select.Option>
                            )}
                            {costCenters.map(({ _id, id, name }, index) => (
                                <Select.Option key={index} value={_id} id={`costcenter-option-${index}`} label={`ID ${id} - ${name}`}>
                                    {showCostCentersIds ? `ID ${id} - ${name}` : name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            {(allowCostCenter2Creation || costCenters2.length > 0) && (
                <Row gutter={[16, 16]} align="middle">
                    <Col span={allowCostCenter2Creation ? 22 : 24}>
                        <Form.Item label={t("label:cost_center_2")} key="costCenter2" name="costCenter2">
                            <Select
                                onSelect={onCostCenter2Change}
                                showSearch
                                filterOption={doFilterSelect}
                                getPopupContainer={getPopupAnchor()}
                                value={expense.costCenter2 || NOT_SET_VALUE}
                                disabled={!isCostCenter2Changeable}
                                placeholder={!isCostCenter2Changeable ? t("label:splitted") : t("placeholder:cost_center_2.select_a_cost_center_2")}
                                notFoundContent={t("missing:no_cost_center_2")}
                            >
                                <Select.Option value={NOT_SET_VALUE} label="">
                                    <div className="flex items-center">
                                        <span>{t("missing:request.cost_center_2")}</span>
                                    </div>
                                </Select.Option>
                                {costCenters2.map((costCenter2, index) => (
                                    <Select.Option key={costCenter2._id} value={costCenter2._id} label={costCenter2.name} id={`costcenter2-option-${index}`}>
                                        {costCenter2.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    {allowCostCenter2Creation && (
                        <Col span={2}>
                            <Form.Item className="mt-10">
                                <Button className="btn-default mt-22" onClick={() => showCostCenter2Modal()}>
                                    <PlusIcon />
                                </Button>
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            )}
            <Row gutter={[16, 16]} align="middle">
                {workflow && (
                    <Col span={12} sm="24">
                        <Form.Item label={t("input:request.workflow")} key="workflow" name="workflow" required>
                            <Select className="mb-5" onSelect={onWorkflowChange} showSearch filterOption={doFilterSelect} getPopupContainer={getPopupAnchor()} disabled={true}>
                                <Select.Option value={workflow._id} label={workflow.name}>
                                    {workflow.name}
                                </Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                )}
                <Col span={workflow ? 12 : 24} sm="24">
                    <Form.Item
                        label={
                            workflow ? t(`input:request.next_approval${secondApprovalNeededBy && !isActiveRuleApproversEqual ? "s" : ""}`) : t("input:request.approval_needed_by")
                        }
                        key="approvalNeededBy"
                        name="approvalNeededBy"
                        rules={approvalNeededBy}
                        initialValue={approvalNeededByValue}
                    >
                        <Select
                            showSearch
                            filterOption={doFilterSelect}
                            value={approvalNeededByValue}
                            onChange={onApproverChange}
                            getPopupContainer={getPopupAnchor()}
                            disabled={isApproverChangeDisabled}
                        >
                            {proposedApprovers.map((employee, index) => {
                                return (
                                    <Select.Option key={index} label={`${employee.firstName} ${employee.lastName}`} value={employee.id}>
                                        <div className="flex items-center" id={`approver-option-${index}`}>
                                            <UserImage user={employee} />
                                            <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                            {employee?.activeCompanyProfile.deleted && (
                                                <Tooltip
                                                    getPopupContainer={getTooltipPopupContainer}
                                                    autoAdjustOverflow={false}
                                                    placement="right"
                                                    title={t("info:employee_deleted")}
                                                    color={ORANGE}
                                                >
                                                    <ExclamationCircleTwoTone twoToneColor={ORANGE} className="ml-2" />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </Select.Option>
                                )
                            })}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            {secondApprovalNeededBy && !isActiveRuleApproversEqual && (
                <>
                    <Row>
                        <Col className="mb-20 -mt-24" span={12} sm="24" offset={12}>
                            <WorkflowConditionTag condition={activeRule?.condition} />
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]} align="middle">
                        <Col span={12} sm="24" offset={12}>
                            <Form.Item key="secondApprovalNeededBy" name="secondApprovalNeededBy" initialValue={secondApprovalNeededBy.id}>
                                <Select
                                    showSearch
                                    filterOption={doFilterSelect}
                                    value={secondApprovalNeededBy.id}
                                    onChange={onApproverChange}
                                    getPopupContainer={getPopupAnchor()}
                                    disabled={isApproverChangeDisabled}
                                >
                                    {proposedApprovers.map((employee, index) => {
                                        return (
                                            <Select.Option key={index} label={`${employee.firstName} ${employee.lastName}`} value={employee.id}>
                                                <div className="flex items-center" id={`second-approver-option-${index}`}>
                                                    <UserImage user={employee} />
                                                    <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                                </div>
                                            </Select.Option>
                                        )
                                    })}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}

            {isAdmin(loggedInUser) && (
                <FormSelect
                    name="requestedBy"
                    header={t("input:request.requested_by")}
                    initialValue={loggedInUser.id}
                    isDisabled={!!expense.card}
                    rules={requestedBy}
                    options={employees.filter((employee: Employee) => employee.email !== FINWAY_ADMIN_EMAIL && !employee.activeCompanyProfile.deleted)}
                    extra={expense.card && <p className="input-help">{t("info:cards.cannot_change_requester_with_attached_card")}</p>}
                    onChange={onRequestedByChange}
                    displayImages
                    showSearch
                />
            )}
        </>
    )
}

export default ExpenseCommonInputFieldsForm
